import React, { useState, useRef, useEffect } from 'react';
import './CollageImage.css';

export interface ClickableItem {
    image: string;
    x: number; // percentage from left
    y: number; // percentage from top
    width: number; // percentage of container width
    link: string;
    alt?: string;
}

export interface CollageImageProps {
    baseImage: string;
    baseImageLowRes?: string; // Optional low-res version for progressive loading
    clickableItems: ClickableItem[];
    alt?: string;
    className?: string;
}

export const CollageImage: React.FC<CollageImageProps> = ({
    baseImage,
    baseImageLowRes,
    clickableItems,
    alt = 'Collage',
    className = ''
}) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [isInView, setIsInView] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });

    // Intersection Observer for lazy loading
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsInView(true);
                        observer.disconnect();
                    }
                });
            },
            {
                rootMargin: '200px', // Start loading 200px before entering viewport
                threshold: 0.01
            }
        );

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => observer.disconnect();
    }, []);

    // Track container dimensions for responsive positioning
    useEffect(() => {
        if (!containerRef.current) return;

        const updateDimensions = () => {
            if (containerRef.current) {
                const { width, height } = containerRef.current.getBoundingClientRect();
                setContainerDimensions({ width, height });
            }
        };

        updateDimensions();

        const resizeObserver = new ResizeObserver(updateDimensions);
        resizeObserver.observe(containerRef.current);

        return () => resizeObserver.disconnect();
    }, [isLoaded]);

    const handleImageLoad = () => {
        setIsLoaded(true);
    };

    const handleItemClick = (e: React.MouseEvent, link: string) => {
        e.preventDefault();
        window.open(link, '_blank', 'noopener,noreferrer');
    };

    return (
        <div 
            ref={containerRef}
            className={`collage-container ${className}`}
        >
            {/* Low-res placeholder (loads first) */}
            {baseImageLowRes && !isLoaded && isInView && (
                <img
                    src={baseImageLowRes}
                    alt={alt}
                    className="collage-base-image collage-placeholder"
                    loading="lazy"
                />
            )}

            {/* High-res base image */}
            {isInView && (
                <img
                    src={baseImage}
                    alt={alt}
                    className={`collage-base-image ${isLoaded ? 'loaded' : 'loading'}`}
                    onLoad={handleImageLoad}
                    loading="lazy"
                />
            )}

            {/* Clickable items overlay */}
            {isLoaded && clickableItems.map((item, index) => {
                // Calculate aspect ratio to maintain item proportions
                const aspectRatioHeight = (containerDimensions.width * item.width) / 100;

                return (
                    <div
                        key={index}
                        className="clickable-item-wrapper"
                        style={{
                            left: `${item.x}%`,
                            top: `${item.y}%`,
                            width: `${item.width}%`,
                        }}
                        onClick={(e) => handleItemClick(e, item.link)}
                    >
                        <img
                            src={item.image}
                            alt={item.alt || `Clickable item ${index + 1}`}
                            className="clickable-item-image"
                            draggable={false}
                        />
                    </div>
                );
            })}
        </div>
    );
};
